/**
 * The Security Center's pages (/security/<slug>): how Defera protects
 * accounts, payments and data, how to spot a scam, and how to report a
 * vulnerability. Bylined by Silas, our SI for security and reliability.
 *
 * THE ONE RULE: every sentence here is true today, and was checked against
 * the code, the live site or the production database before it was written
 * (2026-10-06). A security page that overstates is worse than none, so:
 *
 * - Never add a certification, audit, test or feature we don't have (SOC 2,
 *   ISO 27001, "PCI compliant" Defera, penetration tests, two-factor sign-in,
 *   "bank-level" anything). security-content.test.js fails on them
 *   (FALSE_CLAIM_PATTERNS in guardrails.js).
 * - Every promise a page makes is in the concierge's knowledge base too
 *   (website-promise parity; kb-refs.js and security-content.test.js).
 * - No vendor names. Pages describe what a provider does, not who it is.
 *
 * Bodies use the Help Center's Markdown and tokens (src/content/help/
 * markdown.js, tokens.js). Relative imports only: the build-time
 * pre-renderer reads this file in plain Node.
 */

export const SECURITY_PAGES_UPDATED = '2026-10-23';

/** The hub's "at a glance" facts. Each one is backed by a page below. */
export const SECURITY_HIGHLIGHTS = Object.freeze([
  {
    icon: 'CreditCard',
    title: 'Card numbers never reach us',
    text: 'Cards go straight into our payment processors’ secure card fields. We never store your complete card details.',
    href: '/security/payments',
  },
  {
    icon: 'Lock',
    title: 'Encrypted in transit and at rest',
    text: 'HTTPS with HSTS on every connection, and AES-256 encryption on the data we store.',
    href: '/security/data-protection',
  },
  {
    icon: 'Database',
    title: 'Row-level security on every table',
    text: 'Our database checks every request, so your sign-in only ever opens your own account.',
    href: '/security/data-protection',
  },
  {
    icon: 'Activity',
    title: 'Checked every six hours',
    text: 'An automated security sweep checks our site and services around the clock and alerts our team.',
    href: '/security/infrastructure',
  },
  {
    icon: 'PhoneCall',
    title: 'Verified before we share',
    text: 'On the phone, we confirm who you are before we discuss anything about your account.',
    href: '/security/account-protection',
  },
  {
    icon: 'ShieldCheck',
    title: 'We never ask for your password',
    text: 'Or your full card number. Not by email, text, phone or chat.',
    href: '/security/avoiding-scams',
  },
]);

export const SECURITY_PAGES = Object.freeze([
  {
    slug: 'payments',
    title: 'Payment security',
    navTitle: 'Payment security',
    icon: 'CreditCard',
    summary: 'Your card number goes straight into our payment processors’ secure card fields and never reaches our servers. Here’s how we protect every payment.',
    updated: SECURITY_PAGES_UPDATED,
    body: `When you pay Defera, your card number and security code go straight from your screen into our payment processors' secure card fields. They never reach Defera's servers, and we never store your complete card details.

Here's what happens to your card at each step, from checkout to your last payment.

## When you enter your card

- **Secure card fields.** The card form at checkout and in **Update Card** is hosted by our payment processor. What you type goes directly to the processor over an encrypted connection.
- **A token instead of your card number.** The processor gives us a secure token that stands in for your card. We charge your scheduled payments with that token, never with your card number. This is called tokenization.
- **Certified processors.** Payments are processed by certified PCI-DSS payment gateways. PCI DSS is the card industry's security standard.

## What we keep, and what we don't

- **What we keep:** your card's brand, last 4 digits and expiration date, so you can see which card is on file, along with the secure token and your payment history.
- **What we never keep:** your full card number or its security code (CVC).

## Changing your card

- **Saved cards are replaced, not edited.** For security, a saved card can't be changed in place. **Update Card** takes the full card details in one secure flow and replaces the old card.
- **One place to change it.** **Update Card** is the only place to change your card: open **Trips**, tap your trip, then tap **Update Card** in the Payment Schedule section.
- **Nothing changes if an update fails.** If an update doesn't go through, the card already on file keeps being charged on schedule.

## Behind every payment

- **Verified payment messages.** When a payment processor tells our systems that a payment went through or failed, we check the message's signature before we act on it, so a forged message is ignored.
- **Refunds go back to the card you paid with.** A refund can't be redirected to a different card, a bank account or anyone else.
- **Every payment is on your trip.** Your trip's Payment Schedule shows your payments, so you can check any charge against it.

## How you pay, and how you never pay

We take debit and credit cards. We'll never ask you to pay for a Defera trip by wire transfer, bank transfer, payment app, gift card or cryptocurrency. If someone asks you to pay that way, it isn't Defera. [Avoiding scams](/security/avoiding-scams) explains what to do.

## See a charge you don't recognize?

Start by checking it against your trip's Payment Schedule. [A charge I don't recognize](/help/payments/unrecognized-charge) shows how. If something still looks wrong, [chat with the Defera Concierge](/support) or send us a message from the [Contact page](/contact#message). If you think your card details were stolen, call your bank too.`,
  },
  {
    slug: 'account-protection',
    title: 'Account protection',
    navTitle: 'Account protection',
    icon: 'KeyRound',
    summary: 'How we protect your sign-in and your trips, how you can keep your account safe, and what to do right away if something on your account looks wrong.',
    updated: SECURITY_PAGES_UPDATED,
    body: `Your Defera account holds your trips, your travelers and your payment plan, so we protect it at every step. Nobody at Defera can see your password, and on the phone we check who you are before we discuss your account.

Here's how we protect your account, what you can do to help, and what to do if something looks wrong.

## How we protect your sign-in

- **Your password stays private.** Passwords are handled by our sign-in provider and stored only as a one-way hash, a scrambled form that can't be turned back into your password. Nobody at Defera can see it.
- **Encrypted sign-in.** You always sign in over an encrypted HTTPS connection.
- **Sessions that expire.** Once you're signed in, your session uses secure tokens that expire and renew automatically. When a session expires, you're signed out.
- **Reset links go only to you.** A password reset link is only ever sent to the email address on your account. The reset screen doesn't say whether an email address has a Defera account, so it can't be used to find out who our customers are.
- **Your sign-in email is protected.** The email on your account is your sign-in identity, so it can't be changed from your Profile. If you need it changed, contact us and our team will take a look.

## How we protect your trips

- **Your sign-in opens only your account.** Our database only lets a signed-in customer see their own account, trips and conversations.
- **Trip documents go to your account email.** We only send trip documents to the email address on your account.
- **Emails show only part of your ID numbers.** Your booking confirmation email shows only the last 4 digits of passport, ID and Known Traveler numbers. The full numbers are on your trip, in Flight Details.
- **We check who's calling.** Before the Defera Concierge shares any account details on a call, it asks for your booking reference (or the email on your account) and the passenger's name. The number you're calling from is never enough on its own.

## What you can do

- **Use a strong password you don't use anywhere else.** A password manager makes that easy.
- **Look after your email account.** Password reset links go to it, so whoever controls your email can reset your Defera password. Give your email account a strong password of its own, and turn on two-step verification for that email account if your provider offers it.
- **Sign out on shared devices.** On a computer or phone other people use, tap **Sign Out** when you're done.
- **Keep your password to yourself.** We'll never ask you for it, by email, text, phone or chat.
- **Share a trip, not your sign-in.** To share a trip with a travel companion or an employer, download its Trip Statement from the trip's header row and forward the PDF.

## If something looks wrong

If you see a booking, payment or change on your account that you don't recognize, act quickly:

1. **Reset your password.** Tap **Forgot password?** on the sign-in page and follow the secure link we email you.
2. **Secure your email account.** If you think someone got into your email, change that password too.
3. **Contact us.** [Chat with the Defera Concierge](/support), our AI concierge, or call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), and tell us what you saw. It can send a request to our team to take a look. You can also send us a message from the [Contact page](/contact#message).
4. **Call your bank if your card is involved.** Your bank can block the card and check other charges.

[Keeping your account secure](/help/account/account-security) and [Signing in and resetting your password](/help/account/sign-in-and-password) in our Help Center have more.`,
  },
  {
    slug: 'data-protection',
    title: 'Data protection',
    navTitle: 'Data protection',
    icon: 'Lock',
    summary: 'Your data is encrypted in transit and at rest, protected by row-level security on every table, backed up every day and stored in the United States.',
    updated: SECURITY_PAGES_UPDATED,
    body: `We protect your personal information at every step. It's encrypted on its way to us and while it's stored, our database lets each customer see only their own records, and it's backed up every day.

This page explains how we protect your data. Our [Privacy Policy](/privacy) explains what we collect, how we use it and your rights over it.

## Encrypted in transit

- **HTTPS only.** Every connection to defera.app is encrypted with TLS. If you type an http:// address, you're sent to the secure https:// version.
- **HSTS.** Our site tells your browser to connect to us only over HTTPS, so later visits can't be quietly downgraded to an unencrypted connection.

## Encrypted and backed up at rest

- **Encrypted storage.** Our database provider encrypts all stored data with AES-256.
- **Daily backups.** Our database is backed up automatically every day.
- **Stored in the United States.** Our main database is hosted in the western United States.

## Locked down by default

- **Row-level security on every table.** Our database checks each request against the person making it, so a customer's sign-in only ever opens their own account, trips and conversations.
- **Server-only records.** Our most sensitive internal records can only be reached by our own servers, never directly from a browser or app.
- **Keys kept out of our code.** The keys and passwords our systems use are kept in our hosting providers' protected settings, never in our source code.
- **Secrets scrubbed from logs.** Our logging tools automatically remove passwords, access tokens and keys from what they record.

## Less data, shown less often

- **No complete card details.** Card numbers go straight to our payment processors, and we keep only the card's brand, last 4 digits and expiration date. [Payment security](/security/payments) explains how.
- **Only part of your ID numbers in emails.** Booking confirmation emails show only the last 4 digits of passport, ID and Known Traveler numbers.
- **What we ask for, and why.** We ask for the traveler details a flight booking needs. [Your privacy and your data](/help/account/privacy-and-data) lists each one and why we need it.

## Your privacy rights

Our [Privacy Policy](/privacy) explains your rights over your personal data, including access, correction and deletion, and how to use them. To make a request, send us a message from the [Contact page](/contact#message). We verify your identity before we act on a request. To close your account, see [Closing your account](/help/account/delete-account).

To opt out of the sale or sharing of your personal information with our advertising partners, use [Your privacy choices](/privacy-choices). We also treat a Global Privacy Control signal from your browser as an opt-out.

## If something goes wrong

If a security incident affects your personal information, we'll notify you as the law requires, explaining what happened, what information was involved and what we're doing about it.`,
  },
  {
    slug: 'infrastructure',
    title: 'Infrastructure and monitoring',
    navTitle: 'Infrastructure and monitoring',
    icon: 'Server',
    summary: 'Defera runs on established cloud platforms with protections at every layer, and an automated security sweep checks our site and services every six hours.',
    updated: SECURITY_PAGES_UPDATED,
    body: `Defera runs on established cloud platforms, with protections built in at every layer and automated checks running around the clock. An automated security sweep checks our site and services every six hours and alerts our team to anything critical.

## Where Defera runs

- **Cloud hosting.** Our website and API run on a major cloud hosting platform, served only over HTTPS.
- **Managed database.** Our database, sign-in and file storage run on a managed database platform in the western United States, encrypted at rest and backed up every day.
- **Payment processors.** Card payments run through certified payment processors, so card numbers never pass through our own servers.

## Protections built in

- **HTTPS and HSTS** on every page and every request.
- **Browser protections.** Our pages tell browsers not to let other websites show them inside a frame, which blocks clickjacking tricks, and not to guess at file types.
- **Rate limits.** Our API, flight search and chat limit how many requests can be made in a short time, which slows down automated abuse.
- **Signed payment messages.** Messages from our payment processors are checked for a valid signature before we act on them.
- **Private by default.** Account pages, internal tools and private data turn away anyone who isn't signed in with the right access.

## Automated checks, around the clock

Silas, our SI for security and reliability, runs an automated security sweep every six hours. Each run checks that:

- our site and services are up and loading correctly;
- our site sends the security headers it should;
- private pages and internal tools turn away anyone who isn't signed in;
- our database refuses anonymous requests for private data;
- the settings our security depends on are in place;
- our security certificate is valid and nowhere near expiring.

A new critical finding alerts our team the moment it's found. Every scheduled job, from payment reminders to reports, also checks in each time it runs, and an hourly audit watches our payment operations and those check-ins.

## Testing and change control

- **Automated tests.** Thousands of automated tests cover our payment logic and access checks.
- **Version control.** Every change to Defera's code is tracked in version control, so we always know what changed, when and why.

## When something goes wrong

When a check fails or someone reports a problem, we investigate, contain it, fix the cause and add checks so it doesn't happen again. If an incident affects your personal information, we'll notify you as the law requires. To report a security issue, see [Reporting a vulnerability](/security/responsible-disclosure).`,
  },
  {
    slug: 'ai-and-your-data',
    title: 'AI and your data',
    navTitle: 'AI and your data',
    icon: 'Sparkles',
    summary: 'The Defera Concierge is AI. Here’s what it can see when it helps you, what it can and can’t do, and how your conversations are kept.',
    updated: SECURITY_PAGES_UPDATED,
    body: `The Defera Concierge, in chat and on the phone, is our AI concierge. When you're signed in, it can look at your trips to answer your questions, but it never sees your password or your full card number, and it can't cancel, refund or change a booking.

Here's what that means for your data.

## Who you're talking to

- **The Defera Concierge is AI.** Our chat window says so, and so does our Help Center.
- **Our SI leaders are AI too.** Parts of Defera are led by our super intelligence agents: AI that each lead a part of the business and report to our leadership team. These Security pages are written by Silas, who leads security and reliability. You can [meet them](/about).

## What the concierge can see

- **Your trips, once you're signed in.** In chat, it can see your trips: the itinerary, travelers' names, trip status and payment schedule, so it can answer questions about them.
- **Nothing on the phone until you're verified.** On a call, it shares nothing about your account until you've given your booking reference (or the email on your account) and the passenger's name.
- **What it doesn't see.** Its view of your trips leaves out dates of birth, passport numbers, Known Traveler and frequent flyer numbers, and travelers' email addresses and phone numbers. It never sees your password or your full card number, because Defera doesn't have them either.

## What the concierge can do

- **Answer questions** about Defera and about your trips.
- **Search flights** and show you options. You always complete checkout yourself.
- **Pass a request to our team** when something needs our team's help. You'll see the request as a card on your trip page.
- **What it can't do:** it can't change a booking, issue a refund or charge your card.
- **Cancelling on a call:** on the phone, our concierge can cancel a trip for you, using only the options your trip's Cancel screen offers, and only after you read back a 6-digit code we email to the address on your account. Nothing is cancelled without that code. In chat, it can't cancel.

## Your conversations

- **Saved to your account.** When you're signed in, your chats are saved to your account, so you can pick up where you left off. Other customers can never see them.
- **Not signed in?** A chat you have without signing in isn't saved to an account.
- **Reviewed for quality.** Our team, including our AI quality reviewers, reads conversations to check that answers are accurate and to improve our service.
- **Processed by our AI providers.** To write each reply, the concierge sends your message, along with the trip details described above, to the AI providers that power it.

> [!IMPORTANT] The concierge never needs your password, card number, passport number or Social Security number, so please don't type them into a chat or say them on a call.

## Questions about your data

Our [Privacy Policy](/privacy) explains your rights over your personal data. For questions about your conversations, send us a message from the [Contact page](/contact#message).`,
  },
  {
    slug: 'avoiding-scams',
    title: 'Avoiding scams',
    navTitle: 'Avoiding scams',
    icon: 'ShieldAlert',
    summary: 'What real Defera messages look like, what we’ll never ask you for, and what to do if a message, call or website claiming to be Defera doesn’t look right.',
    updated: SECURITY_PAGES_UPDATED,
    body: `Scammers sometimes pretend to be travel companies, so here's the simplest rule: if a message about your trip asks you to do something, open Defera yourself and check your trip there. What's real is always on your trip page.

## What we'll never ask you for

- **Your password,** by email, text, phone or chat.
- **Your full card number or security code,** by email, text, phone or chat. Card details only ever go into the secure card form inside Defera, at checkout or in **Update Card**.
- **Payment by wire transfer, bank transfer, payment app, gift card or cryptocurrency.** We take debit and credit cards only.
- **Remote access to your device.** We'll never ask you to install an app or share your screen so we can fix your account.

If someone asks you for any of these, it isn't Defera. Stop, don't reply, and contact us.

## How to check a message

1. **Don't use its links or buttons.** Open the Defera app, or type defera.app into your browser yourself.
2. **Sign in and open the trip.** Your booking details, Payment Schedule and requests are all on your trip page.
3. **Compare.** If what the message says isn't on your trip page, contact us before you act on it.

## What real Defera messages look like

- **Booking emails** come from bookings@mail.defera.app: your booking confirmation, and "Your trip has been paid off!" once our team releases your confirmation number.
- **Answers to your requests** also appear on the request's card on your trip page, so you can check one there.
- **Our website is defera.app.** Check the address in your browser before you sign in or enter card details.
- **Our phone line is [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}).** If someone calls claiming to be Defera and you didn't ask for a call, hang up and call that number yourself.

[How to tell an email is really from Defera](/help/emails/is-this-email-from-defera) has more on checking emails.

## If you already clicked or replied

We know that's a worrying moment. Here's what to do:

1. **If you entered your Defera password,** reset it with **Forgot password?** on the sign-in page.
2. **If you entered card details,** call your bank and ask them to block the card.
3. **Contact us** so our team can check your account. [Chat with the Defera Concierge](/support), call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}) or send us a message from the [Contact page](/contact#message).

## Report a scam

If a message, call or website pretends to be Defera, forward it, or a screenshot of it, to [{{SECURITY_EMAIL}}](mailto:{{SECURITY_EMAIL}}). Please leave out your password and full card number.`,
  },
  {
    slug: 'responsible-disclosure',
    title: 'Reporting a vulnerability',
    navTitle: 'Report a vulnerability',
    icon: 'Bug',
    summary: 'Found a security issue in Defera? Report it to our security team. Our disclosure policy covers scope, testing rules, safe harbor and how we say thanks.',
    updated: SECURITY_PAGES_UPDATED,
    body: `If you think you've found a security vulnerability in Defera, please report it to [{{SECURITY_EMAIL}}](mailto:{{SECURITY_EMAIL}}). We welcome reports from security researchers and customers alike, and we'll work with you to understand and fix the problem.

This policy explains how to report, what's in scope, how to test safely, and the protection we give to anyone who follows it.

## How to report

Email [{{SECURITY_EMAIL}}](mailto:{{SECURITY_EMAIL}}) with:

- **What you found,** and where: the page, URL or feature involved.
- **How to reproduce it,** step by step, with any screenshots or proof-of-concept code.
- **The impact,** as you understand it: what someone could do with it.
- **How to reach you,** and whether you'd like to be thanked publicly, and under what name.

One issue per report helps us move faster. Please don't include other people's personal data in a report. If you came across some, describe it instead.

## What happens next

- **We aim to acknowledge your report within 3 business days.**
- **We'll keep you updated** while we investigate and fix it, and we'll let you know when it's fixed.
- **We may ask you questions** to make sure we understand the issue.
- **We'll thank you, if you'd like.** Once the issue is fixed, and with your permission, we'll credit you publicly by name or handle.

## What's in scope

In scope: defera.app and www.defera.app, including the Defera web app and its API under defera.app/api.

Out of scope:

- **Services we use but don't run,** such as our payment processors, email delivery, hosting and AI providers. Please report issues in those to the provider.
- **Social engineering,** phishing, or physical attacks against Defera, our team or our customers.
- **Denial of service,** load testing, or anything else that degrades the service for others.
- **Findings without a demonstrated impact,** such as raw scanner output, missing best-practice headers on their own, email sender settings (SPF, DKIM, DMARC) or software version banners.
- **Low-risk issues** such as self-XSS, sign-out CSRF, or clickjacking on pages with no sensitive actions.
- **AI answers.** Getting our AI concierge to say something wrong or off-topic is out of scope, unless it exposes another customer's data or takes an action it shouldn't.

## Rules for testing

To stay within this policy:

- **Use only accounts you own,** or test accounts you create yourself. Never access, change or delete another person's account or data.
- **If you reach someone else's data, stop.** View no more than you need to confirm the issue, don't keep or share it, and tell us right away.
- **Don't spend real money or use real travelers' details.** Don't use a card that isn't yours, and don't place bookings you don't intend to keep.
- **Keep automated testing gentle.** Respect our rate limits, and stop if you notice the service slowing down.
- **Give us time to fix it.** Keep the issue confidential until it's fixed or 90 days have passed since your report, whichever comes first, and tell us before you publish. If we need more time, we'll explain why and agree a date with you.

## Safe harbor

We consider security research that follows this policy to be authorized, and we won't take legal action against you, or ask law enforcement to act against you, because of it. Specifically:

- **This policy is our written authorization** for research that follows it. We won't treat that research as a breach of our [Terms of Service](/terms), including its sections on unauthorized system access and prohibited technical activities, and we waive those restrictions to the extent they would otherwise apply to it.
- **We consider it authorized under anti-hacking laws,** done in good faith, and helpful to the security of Defera and our customers.
- **If someone else takes legal action** against you over research that follows this policy, we'll make it known that we authorized it.
- **If you're not sure** whether something is allowed, ask us at [{{SECURITY_EMAIL}}](mailto:{{SECURITY_EMAIL}}) before you go ahead.

Safe harbor covers Defera's own systems only. Services run by others have their own policies, and we can't authorize testing of them. It doesn't cover activity that breaks this policy, such as viewing more of other people's data than you need or disrupting the service.

## Rewards

We don't offer cash rewards for reports. What we offer is our thanks: once an issue is fixed, and with your permission, we'll credit you publicly.

## Not a vulnerability?

- **A problem with your account,** like a charge you don't recognize: see [Account protection](/security/account-protection), or [contact us](/contact).
- **A message pretending to be Defera:** see [Avoiding scams](/security/avoiding-scams).

Our [security.txt](https://defera.app/.well-known/security.txt) file lists this contact for security tools.`,
  },
]);

export const SECURITY_PAGE_SLUGS = Object.freeze(SECURITY_PAGES.map((p) => p.slug));
